import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button, Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import { InvestorAutocomplete } from "components/Autocompletes/InvestorAutocomplete";
import { GroupSelectionValue, InvestorGroupAutocomplete } from "components/Autocompletes/InvestorGroupAutocomplete";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook } from "hooks";
import { useAccountSelector, useDealActions, useInvestorsSelector } from "store";

type InvestorOption = { id: number; name: string; is_splittable?: boolean };

const ALL_INVESTORS_ID = -1;

interface InvestorPromptResult {
  investor: InvestorOption | null;
  allInvestors: boolean;
  splitByCommitment: boolean;
  investorIds: number[];
  entireCommitment: boolean;
  groupCommitment: boolean;
}

interface Props {
  currentInvestorId: number | null;
  currentAllInvestors?: boolean;
  currentSplitByCommitment?: boolean;
  currentInvestorIds?: number[];
  currentEntireCommitment?: boolean;
  dealId: number | null;
  showGrouped?: boolean;
  onSubmit: (result: InvestorPromptResult) => void;
  onCancel: () => void;
}

export const InvestorPrompt: React.FC<Props> = ({
  currentInvestorId,
  currentInvestorIds,
  currentEntireCommitment,
  currentAllInvestors = false,
  currentSplitByCommitment = false,
  dealId,
  showGrouped = true,
  onSubmit,
  onCancel
}) => {
  const { allow_split_by_group } = useAccountSelector() || {};
  const investors = useInvestorsSelector();
  const { getDealInvestors } = useDealActions();
  const [dealInvestors, setDealInvestors] = useState<InvestorOption[]>([]);
  const [selected, setSelected] = useState<InvestorOption | null>(() => {
    if (currentAllInvestors) return { id: ALL_INVESTORS_ID, name: "All investors" };
    return investors.find((inv) => inv.id === currentInvestorId) ?? null;
  });
  const [splitByCommitment, setSplitByCommitment] = useState(currentSplitByCommitment);
  const [selectedInvestorIds, setSelectedInvestorIds] = useState<number[]>(currentInvestorIds ?? []);
  const [splitByEntireCommitment, setSplitByEntireCommitment] = useState(currentEntireCommitment ?? false);

  const { loading, action: fetchDealInvestors } = useActionHook(
    () => (dealId ? getDealInvestors({ dealId: dealId, with_all_investors: true }) : Promise.resolve([])),
    {
      onSuccess: (data) => {
        if (Array.isArray(data)) {
          const investors = data.map((inv: { id: number; name: string; is_splittable: boolean }) => ({
            id: inv.id,
            name: inv.name,
            is_splittable: inv.is_splittable
          }));
          setDealInvestors(investors);
        }
      }
    }
  );

  useEffect(() => {
    if (dealId) fetchDealInvestors();
  }, [dealId]);

  const preselectedId = selected?.id ?? null;
  const isAllInvestors = selected?.id === ALL_INVESTORS_ID;

  const showSplitCheckbox = useMemo(() => {
    if (!selected) return false;
    if (selected.id === ALL_INVESTORS_ID) {
      const allInv = dealInvestors.find((d) => d.id === ALL_INVESTORS_ID);
      return allInv?.is_splittable !== false;
    }
    const investor = dealInvestors.find((d) => d.id === selected.id) ?? selected;
    return investor?.is_splittable !== false;
  }, [selected, dealInvestors]);

  const handleGroupsChange = useCallback(
    (value: GroupSelectionValue) => {
      setSelectedInvestorIds(value.investorsIds);

      if (value.groupsName.length) {
        setSelected(null);
        setSplitByCommitment(false);
      } else if (value.investorId !== null) {
        const investor = dealInvestors.find((d) => d.id === value.investorId);
        if (investor) {
          setSelected({ id: investor.id, name: investor.name });
        }
        setSplitByEntireCommitment(false);
        setSelectedInvestorIds([]);
      } else {
        setSplitByCommitment(false);
      }
    },
    [dealInvestors]
  );

  return (
    <Stack sx={{ padding: "24px", gap: "20px", minWidth: "427px" }}>
      <Stack gap="4px">
        {allow_split_by_group ? (
          <InvestorGroupAutocomplete
            loading={loading}
            dealInvestors={dealInvestors}
            preselectedInvestorId={preselectedId}
            allInvestorsId={ALL_INVESTORS_ID}
            onChange={(inv) => {
              setSelected(inv ? { id: inv.id, name: inv.name } : null);
              setSplitByCommitment(false);
            }}
            onGroupsChange={handleGroupsChange}
            preselectedInvestorIds={selectedInvestorIds}
            label="Attributed to"
          />
        ) : (
          <InvestorAutocomplete
            loading={loading}
            dealInvestors={dealInvestors}
            preselectedInvestorId={preselectedId}
            allInvestorsId={ALL_INVESTORS_ID}
            onChange={(value) => {
              const investor: InvestorOption | null = value
                ? (dealInvestors.find((inv) => inv.id === value.id) ?? value)
                : null;
              setSelected(investor);
              setSelectedInvestorIds([]);
              if (!investor) {
                setSplitByCommitment(false);
              } else if (investor.is_splittable === false) {
                setSplitByCommitment(false);
              }
            }}
            showGrouped={showGrouped}
            label="Attributed to"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "& fieldset": { borderColor: "#7b92a9" }
              }
            }}
          />
        )}

        {isAllInvestors && !selectedInvestorIds.length && (
          <Typography sx={{ fontSize: "12px", color: "#496682" }}>
            Values will be split across investors by commitment.
          </Typography>
        )}
      </Stack>
      {!isAllInvestors && !selectedInvestorIds.length && (
        <FormControlLabel
          disabled={!showSplitCheckbox}
          control={
            <Checkbox
              checked={splitByCommitment}
              onChange={(_, checked) => setSplitByCommitment(checked)}
              disabled={!showSplitCheckbox}
            />
          }
          label="Derive total from this investor's commitment"
          sx={{ "& .MuiFormControlLabel-label": { fontSize: "16px", color: "#071f35" } }}
        />
      )}
      {Boolean(selectedInvestorIds.length) && (
        <MaterialTooltip
          title="Uses this group's total commitment as a share of the deal total to derive values for all non-group investors."
          isShow
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={splitByEntireCommitment}
                onChange={(_, checked) => setSplitByEntireCommitment(checked)}
                size="small"
              />
            }
            label="Derive values for remaining investors."
            sx={{ "& .MuiFormControlLabel-label": { fontSize: "14px", color: "#071f35" } }}
          />
        </MaterialTooltip>
      )}
      <Stack direction="row" justifyContent="flex-end" gap="8px">
        <Button
          onClick={onCancel}
          sx={{
            borderRadius: "32px",
            border: "1px solid #7b92a9",
            color: "#071f35",
            textTransform: "none",
            padding: "4px 16px",
            fontSize: "16px",
            fontWeight: 400,
            lineHeight: "24px"
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            const hasGroups = selectedInvestorIds.length;
            onSubmit({
              investor: isAllInvestors ? null : selected,
              allInvestors: isAllInvestors,
              splitByCommitment: isAllInvestors || hasGroups ? true : splitByCommitment,
              investorIds: selectedInvestorIds,
              groupCommitment: hasGroups ? !splitByEntireCommitment : false,
              entireCommitment: hasGroups ? splitByEntireCommitment : false
            });
          }}
          sx={{
            borderRadius: "32px",
            bgcolor: "#071f35",
            color: "white",
            textTransform: "none",
            padding: "4px 16px",
            fontSize: "16px",
            fontWeight: 400,
            lineHeight: "24px",
            "&:hover": { bgcolor: "#0a2a45" },
            "&:disabled": { bgcolor: "#bac7ce", color: "white" }
          }}
        >
          Add
        </Button>
      </Stack>
    </Stack>
  );
};
